import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointments, Appointment } from '../../crm-appointments/useData';

export interface CalendarWidgetProps {
  currentDate?: Date;
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({ currentDate = new Date() }) => {
  const navigate = useNavigate();
  const { appointments, isLoading } = useAppointments();

  const [viewDate, setViewDate] = React.useState(currentDate);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const appointmentsByDate = React.useMemo(() => {
    const map = new Map<string, Appointment[]>();
    (appointments || []).forEach((apt: Appointment) => {
      if (apt.clientAppScheduleDateTime) {
        const date = new Date(apt.clientAppScheduleDateTime);
        const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const existing = map.get(dateKey) || [];
        map.set(dateKey, [...existing, apt]);
      }
    });
    return map;
  }, [appointments]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const goToPreviousMonth = () => {
    setViewDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setViewDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setViewDate(new Date());
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  };

  const getAppointmentsForDay = (day: number) => {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return appointmentsByDate.get(dateKey) || [];
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <div className="h-64 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading calendar...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Appointments</h2>
        <button
          onClick={() => navigate('/appointments_calendar_view')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium"
        >
          View Full
        </button>
      </div>
      
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={goToPreviousMonth}
          className="p-2.5 sm:p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation"
          aria-label="Previous month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {monthNames[month]} {year}
          </h3>
          <button
            onClick={goToToday}
            className="px-3 py-1 text-xs text-teal-600 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-900/20 rounded-md transition-colors"
          >
            Today
          </button>
        </div>
        <button
          onClick={goToNextMonth}
          className="p-2.5 sm:p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation"
          aria-label="Next month"
        >
          <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <div className="overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0 touch-pan-x" style={{ minWidth: 0 }}>
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-2 min-w-[240px] w-max max-w-full mx-auto">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 py-0.5 sm:py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 min-w-[240px] w-max max-w-full mx-auto">
        {Array.from({ length: startingDayOfWeek }).map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square min-w-[28px] sm:min-w-[32px]" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const dayAppointments = getAppointmentsForDay(day);
          const today = isToday(day);

          return (
            <div
              key={day}
              onClick={() => navigate('/appointments_calendar_view')}
              className={`aspect-square min-w-[28px] sm:min-w-[32px] p-0.5 sm:p-1 border rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${
                today ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-300 dark:border-teal-700' : 'border-gray-200 dark:border-gray-600'
              }`}
            >
              <div className={`text-[10px] sm:text-xs font-medium mb-0.5 sm:mb-1 ${today ? 'text-teal-600 dark:text-teal-400' : 'text-gray-700 dark:text-gray-300'}`}>
                {day}
              </div>
              {dayAppointments.length > 0 && (
                <div className="space-y-0.5">
                  {dayAppointments.slice(0, 2).map((apt, idx) => (
                    <div
                      key={idx}
                      className="h-1.5 bg-teal-500 rounded text-[0px]"
                      title={apt.clientAppName || 'Appointment'}
                    ></div>
                  ))}
                  {dayAppointments.length > 2 && (
                    <div className="text-[0.6rem] text-gray-500 dark:text-gray-400 font-medium">
                      +{dayAppointments.length - 2}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default CalendarWidget;

